import { useState, useEffect, useRef } from "react";
import { Bell, X, AlertTriangle, Clock, Megaphone, Heart, Crown } from "lucide-react";
import { apiCall } from "../App";

const PRIORITY_STYLES = {
  high: { dot: "bg-red-400", text: "text-red-400", ring: "ring-red-400/30" },
  medium: { dot: "bg-amber-400", text: "text-amber-400", ring: "ring-amber-400/30" },
  low: { dot: "bg-blue-400", text: "text-blue-400", ring: "ring-blue-400/30" },
};

const TYPE_ICONS = {
  sadhana_reminder: Clock,
  action_required: AlertTriangle,
  subscription: Crown,
  broadcast: Megaphone,
};

const NotificationBell = ({ token }) => {
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const fetchNotifications = () => {
    if (!token) return;
    apiCall("GET", "/notifications", null, token).then(r => {
      if (r.success) setNotifications(r.data.notifications || []);
    });
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 5 * 60 * 1000); // refresh every 5 min
    return () => clearInterval(interval);
  }, [token]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const dismiss = async (id) => {
    await apiCall("POST", `/notifications/dismiss/${id}`, null, token);
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const count = notifications.length;
  const hasHigh = notifications.some(n => n.priority === "high");

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)} className="relative p-2 rounded-lg hover:bg-divine-gold/10 transition-all" data-testid="notification-bell">
        <Bell className={`w-5 h-5 ${count > 0 ? "text-divine-gold" : "text-chakra-crown/40"}`} />
        {count > 0 && (
          <span className={`absolute -top-0.5 -right-0.5 w-4.5 h-4.5 flex items-center justify-center rounded-full text-[10px] font-bold ${hasHigh ? "bg-red-500 text-white ring-2 ring-red-500/30" : "bg-divine-gold text-cosmic-void"}`}
            data-testid="notification-count" style={{ minWidth: "18px", height: "18px" }}>
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 max-h-[420px] overflow-y-auto rounded-xl border border-divine-gold/20 bg-cosmic-void shadow-2xl z-50" data-testid="notification-dropdown">
          <div className="sticky top-0 bg-cosmic-void border-b border-divine-gold/10 px-4 py-3 flex items-center justify-between">
            <span className="font-heading text-sm text-divine-gold">Notifications</span>
            <span className="text-chakra-crown/40 text-xs">{count} active</span>
          </div>

          {notifications.length === 0 ? (
            <div className="p-6 text-center">
              <Bell className="w-8 h-8 text-chakra-crown/20 mx-auto mb-2" />
              <p className="text-chakra-crown/40 text-sm">All caught up!</p>
            </div>
          ) : (
            <div className="divide-y divide-divine-gold/10">
              {notifications.map(n => {
                const pstyle = PRIORITY_STYLES[n.priority] || PRIORITY_STYLES.low;
                const Icon = TYPE_ICONS[n.type] || Bell;
                return (
                  <div key={n.id} className="px-4 py-3 hover:bg-divine-gold/5 transition-all group" data-testid={`notification-${n.id}`}>
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${n.priority === "high" ? "bg-red-500/15" : n.priority === "medium" ? "bg-amber-500/15" : "bg-blue-500/15"}`}>
                        <Icon className={`w-4 h-4 ${pstyle.text}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`w-1.5 h-1.5 rounded-full ${pstyle.dot}`} />
                          <span className="text-chakra-crown/90 text-xs font-medium truncate">{n.title}</span>
                        </div>
                        <p className="text-chakra-crown/50 text-xs mt-1 leading-relaxed">{n.message}</p>
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); dismiss(n.id); }}
                        className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-500/20 transition-all shrink-0"
                        data-testid={`dismiss-${n.id}`}>
                        <X className="w-3 h-3 text-chakra-crown/40" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
